/**
 * WatchlistAddDialog (Consumer Portal)
 *
 * Picker for adding loans / applications (the only entity types a consumer
 * pins) to the watchlist. Wraps the design-system `CommandPalette` so the
 * search/list UX matches the global cmd-K experience: search-as-you-type,
 * categorised results, keyboard navigation, chevron-affordance rows.
 *
 * Cross-portal port of Issue #1424 — see business portal's
 * `command-center/WatchlistAddDialog.tsx` for the original.
 */

import IntakeIcon from '@mui/icons-material/Assignment';
import CasesIcon from '@mui/icons-material/FolderShared';
import RateIcon from '@mui/icons-material/TrendingUp';
import {
  CommandPalette,
  type CommandPaletteCategory,
  type CommandPaletteItem,
  useDebouncedValue,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useAddHomePin, useConsumerCases, useMySubmissions } from '../../lib/trpc/hooks';
import type { ConsumerPinKind } from './utils';

export interface WatchlistAddDialogProps {
  open: boolean;
  onClose: () => void;
}

interface RawRow {
  id: string;
  primary: string;
  secondary?: string;
  kind: ConsumerPinKind;
}

interface CaseWireRow {
  id?: string;
  title?: string;
  caseNumber?: string;
  type?: string;
}
interface SubmissionWireRow {
  id?: string;
  status?: string;
  submissionNumber?: string;
  metadata?: { applicantName?: string; loanAmount?: number };
}

function extractCases(data: unknown): CaseWireRow[] {
  // `useConsumerCases` returns `{ data: Case[] }` from the gateway router.
  return ((data as { data?: CaseWireRow[] } | undefined)?.data ?? []) as CaseWireRow[];
}

function extractSubmissions(data: unknown): SubmissionWireRow[] {
  // `mySubmissions` returns `{ submissions: Submission[] }`.
  return ((data as { submissions?: SubmissionWireRow[] } | undefined)?.submissions ?? []) as SubmissionWireRow[];
}

export function WatchlistAddDialog({ open, onClose }: WatchlistAddDialogProps): ReactElement {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 250);
  const addPin = useAddHomePin();

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // `enabled: open` keeps queries paused while the picker is closed.
  // `consumerCases` is filtered server-side by the calling user's businessId,
  // so consumers only ever see their own data.
  const casesQuery = useConsumerCases({ page: 1, limit: 20 }, { enabled: open });
  const submissionsQuery = useMySubmissions({ page: 1, limit: 20 }, { enabled: open });

  const isSearching = casesQuery.isFetching || submissionsQuery.isFetching;

  const rows = useMemo<RawRow[]>(() => {
    const cases: RawRow[] = extractCases(casesQuery.data).map((row) => ({
      id: String(row.id ?? ''),
      primary: row.title ?? row.caseNumber ?? t('home.watchlist.entityType.loan'),
      secondary: row.caseNumber,
      kind: 'loan',
    }));
    const submissions: RawRow[] = extractSubmissions(submissionsQuery.data).map((row) => ({
      id: String(row.id ?? ''),
      primary: row.metadata?.applicantName ?? row.submissionNumber ?? t('home.watchlist.entityType.application'),
      secondary: row.submissionNumber,
      kind: 'application',
    }));
    const combined = [...cases, ...submissions].filter((r) => r.id.length > 0);
    if (!debouncedQuery) return combined;
    // Client-side filter; the consumerCases / mySubmissions endpoints don't
    // accept a search parameter today. The list is already capped at 20 each
    // so the filter is bounded.
    const q = debouncedQuery.toLowerCase();
    return combined.filter(
      (r) => r.primary.toLowerCase().includes(q) || (r.secondary?.toLowerCase().includes(q) ?? false),
    );
  }, [casesQuery.data, submissionsQuery.data, debouncedQuery, t]);

  const handlePin = useCallback(
    (kind: ConsumerPinKind, entityId: string, label: string) => {
      addPin.mutate({ entityType: kind, entityId, label: label.slice(0, 255) }, { onSuccess: () => onClose() });
    },
    [addPin, onClose],
  );

  const items = useMemo<CommandPaletteItem[]>(
    () =>
      rows.map((row) => ({
        id: `${row.kind}:${row.id}`,
        label: row.primary,
        description: row.secondary,
        category: row.kind,
        icon: ENTITY_ICON[row.kind],
        keywords: row.secondary ? [row.secondary] : undefined,
        onSelect: () => handlePin(row.kind, row.id, row.primary),
      })),
    [rows, handlePin],
  );

  const categories = useMemo<CommandPaletteCategory[]>(
    () => [
      { id: 'loan', label: t('home.watchlist.entityType.loan'), icon: <CasesIcon />, priority: 1 },
      { id: 'application', label: t('home.watchlist.entityType.application'), icon: <IntakeIcon />, priority: 2 },
    ],
    [t],
  );

  return (
    <CommandPalette
      open={open}
      onClose={onClose}
      items={items}
      categories={categories}
      placeholder={t('home.watchlist.consumer.addSearchPlaceholder')}
      emptyMessage={t('home.watchlist.consumer.empty')}
      noResultsMessage={t('palette.noResults')}
      onSearchChange={setQuery}
      isSearching={isSearching}
      showKeyboardHints
    />
  );
}

const ENTITY_ICON: Record<ConsumerPinKind, ReactElement> = {
  case: <CasesIcon />,
  loan: <CasesIcon />,
  application: <IntakeIcon />,
  rate: <RateIcon />,
};
